import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { Download, Loader2, School, X } from "lucide-react";
import { db } from "../../firebase";

type CertificateView = {
  name: string;
  institution: string;
  graduationLabel: string;
  certificateURL: string | null;
};

type ProfileView = {
  nomePublico: string;
  title: string;
  bio: string;
  profilePhotoURL: string | null;
  certificates: CertificateView[];
};

type AdminTherapistProfileModalProps = {
  therapistId: string;
  therapistName: string;
  onClose: () => void;
};

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "object" && value !== null && "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function") {
    try {
      return (value as { toDate: () => Date }).toDate();
    } catch {
      return null;
    }
  }
  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatGraduationDate(value: unknown): string {
  const date = toDate(value);
  if (!date) return "Data não informada";
  return `Concluído em ${date.getFullYear()}`;
}

export default function AdminTherapistProfileModal({
  therapistId,
  therapistName,
  onClose,
}: AdminTherapistProfileModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState<ProfileView | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const snap = await getDoc(doc(db, "therapists", therapistId));
        if (cancelled) return;
        if (!snap.exists()) {
          setProfile(null);
          setError("Este constelador ainda não preencheu o perfil.");
          return;
        }
        const data = snap.data();
        const certificates = Array.isArray(data.certificates)
          ? data.certificates.map((cert: Record<string, unknown>) => ({
              name: String(cert?.name || "").trim() || "Formação não informada",
              institution: String(cert?.institution || "").trim() || "Instituição não informada",
              graduationLabel: formatGraduationDate(cert?.graduationDate),
              certificateURL: typeof cert?.certificateURL === "string" && cert.certificateURL ? cert.certificateURL : null,
            }))
          : [];
        setProfile({
          nomePublico: String(data.nomePublico || therapistName || "Nome não definido"),
          title: String(data.title || "").trim(),
          bio: String(data.bio || "").trim(),
          profilePhotoURL: typeof data.profilePhotoURL === "string" ? data.profilePhotoURL : null,
          certificates,
        });
      } catch (err) {
        console.error("Erro ao carregar perfil do terapeuta:", err);
        if (!cancelled) {
          setError("Não foi possível carregar o perfil e os diplomas.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [therapistId, therapistName]);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black bg-opacity-75" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Perfil e diplomas</h3>
              <p className="text-sm text-gray-500">{therapistName}</p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Fechar">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="overflow-y-auto px-6 py-5">
            {loading && (
              <div className="flex items-center gap-2 py-8 text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Carregando perfil...</span>
              </div>
            )}

            {!loading && error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            {!loading && profile && (
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  {profile.profilePhotoURL ? (
                    <img
                      src={profile.profilePhotoURL}
                      alt={profile.nomePublico}
                      className="h-16 w-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-purple-100 text-sm font-semibold text-purple-800">
                      {profile.nomePublico
                        .split(" ")
                        .map((part) => part[0])
                        .join("")
                        .substring(0, 2)
                        .toUpperCase() || "CB"}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{profile.nomePublico}</p>
                    <p className="text-sm text-gray-600">{profile.title || "Título não informado"}</p>
                  </div>
                </div>

                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Sobre</p>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                    {profile.bio || "Ainda não preencheu a bio."}
                  </p>
                </div>

                <div>
                  <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <School className="h-3.5 w-3.5" />
                    Formações e diplomas
                  </p>
                  {profile.certificates.length === 0 ? (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      Nenhum diploma enviado ainda.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {profile.certificates.map((certificate, index) => (
                        <div key={`${certificate.name}-${index}`} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <p className="font-medium text-gray-900">{certificate.name}</p>
                          <p className="text-sm text-gray-600">{certificate.institution}</p>
                          <p className="mt-1 text-xs text-gray-500">{certificate.graduationLabel}</p>
                          {certificate.certificateURL ? (
                            <a
                              href={certificate.certificateURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:underline"
                            >
                              <Download className="h-3.5 w-3.5" />
                              Ver diploma
                            </a>
                          ) : (
                            <p className="mt-3 text-xs text-amber-700">Formação cadastrada, mas o arquivo do diploma não foi enviado.</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
